import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Scale, Upload, FileText, Loader2, Sparkles, Trash2, Link2, RefreshCw, ChevronDown, ChevronRight, Save, AlertTriangle, ClipboardCopy } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const AUTHORITIES = [
  { value: 'committee', label: 'Committee resolution' },
  { value: 'general_meeting', label: 'General meeting (ordinary resolution)' },
  { value: 'special_resolution', label: 'Special resolution (general meeting)' },
  { value: 'board', label: 'Board / executive decision' },
  { value: 'legislation', label: 'Legislation / regulator' },
  { value: 'unknown', label: 'Not specified' },
];
const AUTH_COLOR = {
  committee: '#1F3A5F', general_meeting: '#1c6b2c', special_resolution: '#8a1f11',
  board: '#6b3fa0', legislation: '#9a6b00', unknown: '#7a7a6a',
};
const STATUS_OPTS = [
  { value: 'current', label: 'Current' },
  { value: 'under_review', label: 'Under review' },
  { value: 'superseded', label: 'Superseded' },
];
const RANK = { unknown: 0, committee: 1, board: 2, general_meeting: 3, special_resolution: 4, legislation: 5 };

function PolicyRow({ doc, policy, motions, onSaved }) {
  const { govUpdatePolicy } = useAuth();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(policy);
  const [saving, setSaving] = useState(false);
  useEffect(() => setDraft(policy), [policy]);

  const linkedIds = new Set((draft.matched || []).map((m) => m.motion_id));

  const toggleMotion = (mo) => {
    const has = linkedIds.has(mo.motion_id);
    const next = has
      ? (draft.matched || []).filter((m) => m.motion_id !== mo.motion_id)
      : [...(draft.matched || []), { ...mo, auto: false }];
    setDraft({ ...draft, matched: next });
  };

  const save = async () => {
    setSaving(true);
    try {
      const updated = await govUpdatePolicy(doc.id, policy.id, {
        title: draft.title, summary: draft.summary, authority_required: draft.authority_required,
        reference: draft.reference, content: draft.content, notes: draft.notes, status: draft.status,
        matched_motion_ids: (draft.matched || []).map((m) => m.motion_id),
      });
      toast.success('Policy updated.');
      onSaved(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save the policy.');
    } finally { setSaving(false); }
  };

  const col = AUTH_COLOR[draft.authority_required] || AUTH_COLOR.unknown;
  return (
    <div className="border rounded-lg" style={{ borderColor: 'rgba(0,0,0,0.1)' }} data-testid={`gov-policy-${policy.id}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-start gap-2 p-3 text-left" data-testid={`gov-policy-toggle-${policy.id}`}>
        {open ? <ChevronDown size={16} className="mt-1 flex-shrink-0" /> : <ChevronRight size={16} className="mt-1 flex-shrink-0" />}
        <div className="min-w-0 flex-1">
          <div className="font-title text-sm" style={{ color: 'var(--navy)' }}>{draft.title}</div>
          <div className="flex flex-wrap items-center gap-2 mt-1 text-xs" style={{ color: 'var(--olive)' }}>
            <span className="px-2 py-0.5 rounded-full" style={{ color: col, border: `1px solid ${col}` }}>{AUTHORITIES.find((a) => a.value === draft.authority_required)?.label}</span>
            {draft.reference && <span>{draft.reference}</span>}
            {(draft.matched || []).length > 0 && <span className="inline-flex items-center gap-1" style={{ color: 'var(--maroon)' }}><Link2 size={12} /> {(draft.matched || []).length} motion(s)</span>}
            {policy.has_conflict && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-title" style={{ color: '#8a1f11', background: '#f9e4e0', border: '1px solid #8a1f11' }} data-testid={`gov-policy-conflict-${policy.id}`}><AlertTriangle size={12} /> authority conflict</span>}
            {draft.override && <span className="italic">edited</span>}
          </div>
        </div>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-3 border-t pt-3" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="text-xs" style={{ color: 'var(--olive)' }}>Title
              <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="w-full mt-1 border rounded px-2 py-1.5 text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} data-testid={`gov-policy-title-${policy.id}`} />
            </label>
            <label className="text-xs" style={{ color: 'var(--olive)' }}>Clause / reference
              <input value={draft.reference || ''} onChange={(e) => setDraft({ ...draft, reference: e.target.value })} className="w-full mt-1 border rounded px-2 py-1.5 text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} />
            </label>
          </div>
          <label className="text-xs block" style={{ color: 'var(--olive)' }}>Summary
            <textarea value={draft.summary || ''} onChange={(e) => setDraft({ ...draft, summary: e.target.value })} rows={2} className="w-full mt-1 border rounded px-2 py-1.5 text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} />
          </label>
          <label className="text-xs block" style={{ color: 'var(--olive)' }}>Content (clause wording copied from the document)
            <textarea value={draft.content || ''} onChange={(e) => setDraft({ ...draft, content: e.target.value })} rows={4} placeholder="The verbatim text of this clause / provision." className="w-full mt-1 border rounded px-2 py-1.5 text-sm font-serif" style={{ borderColor: 'rgba(0,0,0,0.2)', background: '#fcfaf4' }} data-testid={`gov-policy-content-${policy.id}`} />
          </label>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="text-xs" style={{ color: 'var(--olive)' }}>Authority required to change
              <select value={draft.authority_required} onChange={(e) => setDraft({ ...draft, authority_required: e.target.value })} className="w-full mt-1 border rounded px-2 py-1.5 text-sm bg-white" style={{ borderColor: 'rgba(0,0,0,0.2)' }} data-testid={`gov-policy-authority-${policy.id}`}>
                {AUTHORITIES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
            </label>
            <label className="text-xs" style={{ color: 'var(--olive)' }}>Status
              <select value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })} className="w-full mt-1 border rounded px-2 py-1.5 text-sm bg-white" style={{ borderColor: 'rgba(0,0,0,0.2)' }}>
                {STATUS_OPTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </label>
          </div>
          <label className="text-xs block" style={{ color: 'var(--olive)' }}>Admin notes
            <textarea value={draft.notes || ''} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} rows={2} className="w-full mt-1 border rounded px-2 py-1.5 text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} />
          </label>
          <div>
            <div className="text-xs font-title mb-1.5" style={{ color: 'var(--navy)' }}>Linked affirmed motions</div>
            <p className="text-[11px] mb-1.5" style={{ color: 'var(--olive)' }}>A motion decided at a lower authority than this policy requires is flagged as a possible invalid decision.</p>
            {motions.length === 0 ? <p className="text-xs" style={{ color: 'var(--olive)' }}>No carried motions available to link yet.</p> : (
              <div className="max-h-44 overflow-y-auto space-y-1 border rounded p-2" style={{ borderColor: 'rgba(0,0,0,0.1)' }}>
                {motions.map((mo) => {
                  const linked = linkedIds.has(mo.motion_id);
                  const clash = linked && (RANK[mo.authority] || 0) < (RANK[draft.authority_required] || 0) && (RANK[draft.authority_required] || 0) > 0;
                  return (
                    <label key={mo.motion_id} className="flex items-start gap-2 text-xs cursor-pointer py-0.5" style={{ color: 'var(--navy)' }}>
                      <input type="checkbox" checked={linked} onChange={() => toggleMotion(mo)} className="mt-0.5" data-testid={`gov-link-motion-${policy.id}-${mo.motion_id}`} />
                      <span><span className="font-medium">{mo.question}</span> <span style={{ color: 'var(--olive)' }}>— {mo.meeting_title} · {mo.authority_label}</span>{clash && <span className="inline-flex items-center gap-0.5 ml-1" style={{ color: '#8a1f11' }}><AlertTriangle size={11} /> conflict</span>}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-title text-white disabled:opacity-60" style={{ background: 'var(--maroon)' }} data-testid={`gov-policy-save-${policy.id}`}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save policy
          </button>
        </div>
      )}
    </div>
  );
}

export default function GovernanceDocsTab() {
  const { govAdminListDocs, govCreateDoc, govExtractDoc, govRematchDoc, govFillContent, govDeleteDoc, govCarriedMotions } = useAuth();
  const [docs, setDocs] = useState([]);
  const [motions, setMotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [openDoc, setOpenDoc] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([govAdminListDocs(), govCarriedMotions().catch(() => [])])
      .then(([d, m]) => { setDocs(d); setMotions(m); })
      .catch(() => toast.error('Could not load governance documents.'))
      .finally(() => setLoading(false));
  }, [govAdminListDocs, govCarriedMotions]);
  useEffect(load, [load]);

  const readFileB64 = (f) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(f);
  });

  const submit = async () => {
    if (!name.trim()) return toast.error('Give the document a name.');
    if (!file && !text.trim()) return toast.error('Upload a file or paste some text.');
    setUploading(true);
    try {
      const body = { name: name.trim() };
      if (file) { body.file_b64 = await readFileB64(file); body.filename = file.name; }
      else { body.text = text; }
      const doc = await govCreateDoc(body);
      toast.success('Document saved. Now extracting policies…');
      setName(''); setText(''); setFile(null);
      // auto-run extraction
      setBusyId(doc.id);
      const extracted = await govExtractDoc(doc.id);
      toast.success(`Extracted ${extracted.policy_count} policies.`);
      setOpenDoc(doc.id);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not process the document.');
      load();
    } finally { setUploading(false); setBusyId(null); }
  };

  const extract = async (id) => {
    setBusyId(id);
    try { const d = await govExtractDoc(id); toast.success(`Extracted ${d.policy_count} policies.`); setOpenDoc(id); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Extraction failed.'); }
    finally { setBusyId(null); }
  };
  const rematch = async (id) => {
    setBusyId(id);
    try { await govRematchDoc(id); toast.success('Motion matches refreshed.'); load(); }
    catch { toast.error('Could not refresh matches.'); }
    finally { setBusyId(null); }
  };
  const fillContent = async (id) => {
    setBusyId(id);
    try {
      const r = await govFillContent(id);
      toast.success(r.filled ? `Clause wording copied into ${r.filled} polic${r.filled === 1 ? 'y' : 'ies'}.` : 'Every policy already has its clause content.');
      load();
    } catch { toast.error('Could not copy clause content.'); }
    finally { setBusyId(null); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this document and its extracted policies?')) return;
    try { await govDeleteDoc(id); toast.success('Deleted.'); load(); }
    catch { toast.error('Could not delete.'); }
  };

  const updateDocInState = (updated) => setDocs((prev) => prev.map((d) => d.id === updated.id ? { ...d, policies: updated.policies } : d));

  return (
    <div className="space-y-6" data-testid="governance-docs-tab">
      <div className="flex items-center gap-2">
        <Scale size={22} style={{ color: 'var(--maroon)' }} />
        <h2 className="font-title text-xl" style={{ color: 'var(--maroon)' }}>Governance document extraction</h2>
      </div>
      <p className="text-sm max-w-2xl" style={{ color: 'var(--olive)' }}>
        Upload a constitution, rules or policy manual (PDF, DOCX or text) — each policy is identified and the
        authority needed to change it, then suggests affirmed committee motions that relate to it. Everything is
        editable, and the results appear on the public <strong>Governance</strong> page.
      </p>

      <div className="bg-white rounded-lg border p-4 space-y-3" style={{ borderColor: 'rgba(0,0,0,0.1)' }}>
        <div className="font-title text-sm" style={{ color: 'var(--navy)' }}>Add a governing document</div>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Document name (e.g. Constitution 2024)" className="w-full border rounded px-3 py-2 text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} data-testid="gov-doc-name" />
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs block mb-1" style={{ color: 'var(--olive)' }}>Upload a file (PDF / DOCX / TXT)</label>
            <input type="file" accept=".pdf,.docx,.txt,.md,application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" data-testid="gov-doc-file" />
            {file && <div className="text-xs mt-1" style={{ color: 'var(--maroon)' }}>{file.name}</div>}
          </div>
          <div>
            <label className="text-xs block mb-1" style={{ color: 'var(--olive)' }}>…or paste the text</label>
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder="Paste the document text here" className="w-full border rounded px-3 py-2 text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} disabled={!!file} data-testid="gov-doc-text" />
          </div>
        </div>
        <button onClick={submit} disabled={uploading} className="inline-flex items-center gap-2 px-4 py-2 rounded font-title text-white disabled:opacity-60" style={{ background: 'var(--maroon)' }} data-testid="gov-doc-submit">
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Upload &amp; extract
        </button>
      </div>

      {loading ? <p className="flex items-center gap-2 text-sm" style={{ color: 'var(--olive)' }}><Loader2 size={15} className="animate-spin" /> Loading…</p>
        : docs.length === 0 ? <p className="text-sm" style={{ color: 'var(--olive)' }} data-testid="gov-docs-empty">No governing documents yet.</p>
        : (
          <div className="space-y-4" data-testid="gov-docs-list">
            {docs.map((doc) => (
              <div key={doc.id} className="bg-white rounded-lg border" style={{ borderColor: 'rgba(0,0,0,0.12)' }} data-testid={`gov-doc-${doc.id}`}>
                <div className="flex items-start justify-between gap-3 p-4 flex-wrap">
                  <button onClick={() => setOpenDoc(openDoc === doc.id ? null : doc.id)} className="flex items-start gap-2 text-left min-w-0" data-testid={`gov-doc-open-${doc.id}`}>
                    <FileText size={18} className="mt-0.5 flex-shrink-0" style={{ color: 'var(--maroon)' }} />
                    <div className="min-w-0">
                      <div className="font-title" style={{ color: 'var(--navy)' }}>{doc.name}</div>
                      <div className="text-xs" style={{ color: 'var(--olive)' }}>
                        {doc.status === 'extracted' ? `${doc.policy_count} policies` : 'Not yet extracted'}
                        {doc.filename ? ` · ${doc.filename}` : ' · pasted text'} · {doc.char_count?.toLocaleString()} chars
                      </div>
                    </div>
                  </button>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={() => extract(doc.id)} disabled={busyId === doc.id} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs font-title text-white disabled:opacity-60" style={{ background: 'var(--navy)' }} data-testid={`gov-doc-extract-${doc.id}`}>
                      {busyId === doc.id ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} {doc.status === 'extracted' ? 'Re-extract' : 'Extract'}
                    </button>
                    {doc.status === 'extracted' && (
                      <button onClick={() => rematch(doc.id)} disabled={busyId === doc.id} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs font-title disabled:opacity-60" style={{ background: 'var(--sage)', color: 'var(--navy)' }} data-testid={`gov-doc-rematch-${doc.id}`}>
                        <RefreshCw size={13} /> Rematch
                      </button>
                    )}
                    {doc.status === 'extracted' && (doc.policies || []).some((p) => !p.content) && (
                      <button onClick={() => fillContent(doc.id)} disabled={busyId === doc.id} title="Copy each clause's wording from the document into the policy Content field" className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs font-title disabled:opacity-60" style={{ background: 'var(--gold)', color: '#3a2c06' }} data-testid={`gov-doc-fill-content-${doc.id}`}>
                        <ClipboardCopy size={13} /> Copy clauses
                      </button>
                    )}
                    <button onClick={() => remove(doc.id)} className="inline-flex items-center gap-1 px-2 py-1.5 rounded text-xs" style={{ background: 'white', border: '1px solid rgba(138,31,17,0.4)', color: 'var(--maroon)' }} data-testid={`gov-doc-delete-${doc.id}`}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
                {openDoc === doc.id && doc.status === 'extracted' && (
                  <div className="px-4 pb-4 space-y-2" data-testid={`gov-doc-policies-${doc.id}`}>
                    {(doc.policies || []).map((p) => (
                      <PolicyRow key={p.id} doc={doc} policy={p} motions={motions} onSaved={updateDocInState} />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
